import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import {
  Braces,
  Database,
  MessageSquareMore,
  Rocket,
  TrendingUp,
} from "lucide-react";
import { Container } from "./layout/container";

import { useLanguage } from "../context/LanguageContext";

const stages = [
  {
    label: "Embed",
    title: "Start inside the real workflow",
    description:
      "Interview users, observe the handoffs, and identify the constraints that a requirements document misses.",
    detail: "Users + workflow",
    icon: MessageSquareMore,
  },
  {
    label: "Model",
    title: "Make the data trustworthy",
    description:
      "Trace the source, define the metric, and align the semantic model before automating decisions on top of it.",
    detail: "SQL + metrics",
    icon: Database,
  },
  {
    label: "Build",
    title: "Turn ambiguity into a working product",
    description:
      "Prototype the interface and AI workflow myself, then use the prototype to accelerate alignment and engineering.",
    detail: "Prototype + AI",
    icon: Braces,
  },
  {
    label: "Deploy",
    title: "Fit the solution into production",
    description:
      "Connect existing systems, add evaluation and guardrails, and design the operational path around the product.",
    detail: "Integration + reliability",
    icon: Rocket,
  },
  {
    label: "Learn",
    title: "Measure adoption, not applause",
    description:
      "Track usage and business outcomes, return to users, and iterate until the product becomes part of the workflow.",
    detail: "Adoption + impact",
    icon: TrendingUp,
  },
];

const stageIcons = [MessageSquareMore, Database, Braces, Rocket, TrendingUp];

export default function ProductSystemMap() {
  const [activeStage, setActiveStage] = useState(0);
  const reduceMotion = useReducedMotion();
  const { strings } = useLanguage();
  const psm = strings?.productSystemMap;

  const currentStages = psm?.stages?.map((stage, i) => ({
    ...stage,
    icon: stageIcons[i] || stages[i]?.icon,
  })) || stages;

  return (
    <section
      id="operating-model"
      className="border-y border-white/[0.08] py-20 sm:py-24 lg:py-28"
      aria-labelledby="operating-model-title"
    >
      <Container>
        <div className="max-w-3xl">
          <p className="text-sm font-medium text-[#8AB4FF]">{psm?.eyebrow || "Forward-deployed operating model"}</p>
          <h2
            id="operating-model-title"
            className="mt-4 text-3xl font-semibold tracking-[-0.035em] text-white sm:text-4xl lg:text-5xl"
          >
            {psm?.title || "From messy problem to adopted product."}
          </h2>
          <p className="mt-5 max-w-2xl text-base leading-7 text-white/65 sm:text-lg">
            {psm?.subtitle || "I stay close to the user, the data, and the implementation from discovery through production."}
          </p>
        </div>

        <div className="mt-14 grid gap-12 lg:grid-cols-[minmax(0,0.9fr)_minmax(360px,1.1fr)] lg:gap-20">
          <div className="space-y-4">
            {currentStages.map((stage, index) => {
              const Icon = stage.icon;
              const isActive = activeStage === index;

              return (
                <motion.article
                  key={stage.label}
                  onViewportEnter={() => setActiveStage(index)}
                  viewport={{ margin: "-45% 0px -45% 0px" }}
                  className={`relative rounded-2xl border px-5 py-6 transition-colors duration-200 sm:px-6 ${
                    isActive
                      ? "border-[#8AB4FF]/45 bg-[#8AB4FF]/8"
                      : "border-white/[0.08] bg-white/[0.025]"
                  }`}
                  aria-current={isActive ? "step" : undefined}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border ${
                        isActive
                          ? "border-[#8AB4FF]/45 bg-[#8AB4FF]/12 text-[#B7D0FF]"
                          : "border-white/10 bg-white/5 text-white/50"
                      }`}
                    >
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-white/45">
                        {stage.label}
                      </p>
                      <h3 className="mt-2 text-lg font-semibold text-white">{stage.title}</h3>
                      <p className="mt-2 text-sm leading-6 text-white/60">{stage.description}</p>
                    </div>
                  </div>
                </motion.article>
              );
            })}
          </div>

          <div className="lg:sticky lg:top-28 lg:h-fit">
            <div className="overflow-hidden rounded-3xl border border-white/10 bg-[#101017] p-5 shadow-2xl shadow-black/30 sm:p-7">
              <div className="flex items-center justify-between border-b border-white/[0.08] pb-5">
                <div>
                  <p className="text-xs font-medium uppercase tracking-[0.16em] text-white/40">
                    {psm?.systemLabel || "Product system"}
                  </p>
                  <p className="mt-1 text-sm text-white/70">{psm?.systemSub || "Problem to production"}</p>
                </div>
                <span className="rounded-full border border-[#8AB4FF]/30 bg-[#8AB4FF]/10 px-3 py-1 text-xs text-[#B7D0FF]">
                  {String(activeStage + 1).padStart(2, "0")} / {String(currentStages.length).padStart(2, "0")}
                </span>
              </div>

              <div className="relative mt-6 space-y-3">
                <div className="absolute bottom-5 left-5 top-5 w-px bg-white/10" aria-hidden="true" />
                <motion.div
                  className="absolute bottom-5 left-5 top-5 w-px origin-top bg-[#8AB4FF]"
                  animate={{
                    transform: `scaleY(${activeStage / (currentStages.length - 1)})`,
                  }}
                  transition={
                    reduceMotion
                      ? { duration: 0.1 }
                      : { duration: 0.45, ease: [0.77, 0, 0.175, 1] }
                  }
                  aria-hidden="true"
                />

                {currentStages.map((stage, index) => {
                  const reached = index <= activeStage;

                  return (
                    <div
                      key={stage.detail}
                      className={`relative flex items-center gap-4 rounded-2xl border px-4 py-4 transition-colors duration-200 ${
                        reached
                          ? "border-white/[0.12] bg-white/[0.055]"
                          : "border-transparent bg-transparent"
                      }`}
                    >
                      <span
                        className={`relative z-10 h-2.5 w-2.5 shrink-0 rounded-full border-2 ${
                          reached
                            ? "border-[#8AB4FF] bg-[#101017]"
                            : "border-white/20 bg-[#101017]"
                        }`}
                        aria-hidden="true"
                      />
                      <div className="min-w-0">
                        <p className={`text-sm font-medium ${reached ? "text-white" : "text-white/35"}`}>
                          {stage.label}
                        </p>
                        <p className={`mt-0.5 text-xs ${reached ? "text-white/55" : "text-white/25"}`}>
                          {stage.detail}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}

export { stages as productSystemStages };
